<template>
  <div class="page t-container">
    <div class="page-heading" role="heading">
      <h1 class="page-heading__header">My Bin Locations</h1>
      <div class="t-mt-4 md:t-mt-0">
        <button
          @click="showDepotBinLocationForm()"
          class="t-align-right t-bg-dodger-blue t-px-8 t-py-3"
        >
          <span class="t-text-base t-font-bold t-text-white">
            <span class="t-text-sm">+</span>
            Assign Booking to Bin Location
          </span>
        </button>
      </div>

      <div class="t-mt-4 md:t-mt-0 t-ml-2">
        <button
          @click="showForm()"
          class="t-align-right t-bg-dodger-blue t-px-8 t-py-3"
        >
          <span class="t-text-base t-font-bold t-text-white">
            <span class="t-text-sm">+</span>
            New Bin Location
          </span>
        </button>
      </div>
    </div>

    <!-- start of bin locations table  -->
    <div class="t-mt-12 t-flex t-justify-center">
      <loading-alert
        v-if="loadingBinLocation"
        text="Loading..."
      ></loading-alert>
      <table
        class="t-w-full t-text-md bg-white t-shadow-md t-rounded t-mb-4"
        v-else
      >
        <tbody>
          <tr class="t-border-b">
            <th class="t-text-left t-p-3 t-px-5">Bin Location Name</th>
            <th class="t-text-left t-p-3 t-px-5">Depot</th>
            <th class="t-p-3 t-px-5 t-flex t-justify-start">Actions</th>
          </tr>
          <tr
            class="t-border-b hover:t-bg-orange-100 t-bg-gray-100"
            v-for="binLocation in binLocations"
            :key="binLocation.id"
          >
            <td class="t-p-3 t-px-5">
              {{ binLocation.name }}
            </td>
            <td class="t-p-3 t-px-5">
              {{ binLocation.depot.name }}
            </td>
            <td class="t-p-3 t-px-5 t-flex t-justify-start">
              <button
                @click="updateBinLocation(binLocation)"
                class="t-mr-3 t-text-sm t-bg-blue-500 hover:t-bg-blue-700 t-text-white t-py-2 t-px-4 focus:t-outline-none focus:t-shadow-outline"
              >
                <i class="fa fa-pencil"></i> Edit
              </button>
              <button
                :disabled="binLocation.depot_bin_locations.length !== 0"
                @click="deleteBinLocation(binLocation)"
                class="t-text-sm t-bg-red-500 hover:t-bg-red-700 t-text-white t-py-2 t-px-4 focus:t-outline-none focus:t-shadow-outline"
                :class="{
                  't-cursor-not-allowed':
                    binLocation.depot_bin_locations.length !== 0
                }"
              >
                <i class="fa fa-trash"></i> Delete
              </button>
            </td>
          </tr>
          <tr
            v-if="binLocations.length === 0"
            class="t-border-b hover:t-bg-orange-100 t-bg-gray-100"
          >
            <td colspan="2" class="t-p-3 t-px-5">
              You don't have any bin locations
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <!-- end of bin locations table  -->

    <!-- start of depot bin locations table  -->
    <div class="t-mt-12 t-flex t-justify-center">
      <loading-alert
        v-if="loadingDepotBinLocation"
        text="Loading..."
      ></loading-alert>
      <table
        class="t-w-full t-text-md bg-white t-shadow-md t-rounded t-mb-4"
        v-else
      >
        <tbody>
          <tr class="t-border-b">
            <th class="t-text-left t-p-3 t-px-5">Bin Location</th>
            <th class="t-text-left t-p-3 t-px-5">
              Current Bookings At This Bin Location
            </th>
            <th class="t-p-3 t-px-5 t-flex t-justify-start">Actions</th>
          </tr>
          <tr
            v-for="depotBinLocation in depotBinLocations"
            :key="depotBinLocation.id"
            class="t-border-b hover:t-bg-orange-100 t-bg-gray-100"
          >
            <td class="t-p-3 t-px-5">
              {{ depotBinLocation.bin_location.name }} ({{
                depotBinLocation.bin_location.depot.name
              }})
            </td>
            <td class="t-p-3 t-px-5">
              {{ depotBinLocation.booking.number }}
            </td>
            <td class="t-p-3 t-px-5 t-flex t-justify-start">
              <button
                @click="updateDepotBinLocation(depotBinLocation)"
                class="t-mr-3 t-text-sm t-bg-blue-500 hover:t-bg-blue-700 t-text-white t-py-2 t-px-4 focus:t-outline-none focus:t-shadow-outline"
              >
                <i class="fa fa-pencil"></i> Edit
              </button>
              <button
                @click="deleteDepotBinLocation(depotBinLocation)"
                class="t-text-sm t-bg-red-500 hover:t-bg-red-700 t-text-white t-py-2 t-px-4 focus:t-outline-none focus:t-shadow-outline"
              >
                <i class="fa fa-trash"></i> Delete
              </button>
            </td>
          </tr>
          <tr
            v-if="depotBinLocations.length === 0"
            class="t-border-b hover:t-bg-orange-100 t-bg-gray-100"
          >
            <td colspan="2" class="t-p-3 t-px-5">
              There's no booking at the bin location
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <!-- end of depot bin locations table  -->

    <bin-locations-form
      v-if="formShowing"
      :bin-location="updatingBinLocation"
      @cancelled="formCancelled()"
      @submitted="formSubmitted()"
    ></bin-locations-form>

    <depot-bin-locations-form
      v-if="depotBinLocationFormShowing"
      :depot-bin-location="updatingDepotBinLocation"
      :bin-locations-data="binLocations"
      @cancelled="depotBinLocationFormCancelled()"
      @submitted="depotBinLocationFormSubmitted()"
    ></depot-bin-locations-form>

    <confirm-modal
      v-if="modalShowing"
      :showing="modalShowing"
      :confirming="confirming"
      title="Confirm delete?"
      text="Are you sure you want to delete ?"
      @confirmed="
        isDeletingBinLocationActive
          ? deleteConfirmed()
          : deleteDepotBinLocationConfirmed()
      "
      @cancelled="
        isDeletingBinLocationActive
          ? deleteCancelled()
          : deleteDepotBinLocationCancelled()
      "
    >
    </confirm-modal>
  </div>
</template>

<script>
import BinLocationsForm from "./form/BinLocationsForm.vue";
import DepotBinLocationsForm from "./form/DepotBinLocationsForm.vue";

export default {
  name: "BinLocations",
  components: {
    BinLocationsForm,
    DepotBinLocationsForm
  },
  props: ["filteredDepot"],
  data() {
    return {
      confirming: false,
      formShowing: false,
      modalShowing: false,
      isDeletingBinLocationActive: false,
      loadingBinLocation: false,
      updatingBinLocation: null,
      binLocations: [],
      deletingBinLocation: null,

      loadingDepotBinLocation: false,
      depotBinLocationFormShowing: false,
      updatingDepotBinLocation: null,
      depotBinLocations: [],
      deletingDepotBinLocation: null
    };
  },
  created() {
    this.loadBinLocations();
  },
  methods: {
    loadBinLocations() {
      this.loadingBinLocation = true;
      this.loadingDepotBinLocation = true;

      if (this.filteredDepot) {
        axios
          .get(`/depot/binlocation?depotId=${this.filteredDepot}`)
          .then(response => {
            this.binLocations = response.data.bin_locations;
            this.depotBinLocations = response.data.depot_bin_locations;
            this.loadingBinLocation = false;
            this.loadingDepotBinLocation = false;
          });
      } else {
        axios.get("/depot/binlocation").then(response => {
          this.binLocations = response.data.bin_locations;
          this.depotBinLocations = response.data.depot_bin_locations;
          this.loadingBinLocation = false;
          this.loadingDepotBinLocation = false;
        });
      }
    },
    showForm() {
      this.formShowing = true;
      this.updatingBinLocation = null;
    },
    formCancelled() {
      this.formShowing = false;
      this.updatingBinLocation = null;
    },
    formSubmitted() {
      this.formShowing = false;
      this.loadBinLocations();
    },
    updateBinLocation(binLocation) {
      this.updatingBinLocation = binLocation;
      this.formShowing = true;
    },
    deleteBinLocation(binLocation) {
      this.modalShowing = true;
      this.deletingBinLocation = binLocation;
      this.isDeletingBinLocationActive = true;
    },

    deleteConfirmed() {
      this.confirming = true;
      axios
        .delete("/depot/binlocation/" + this.deletingBinLocation.id)
        .then(response => {
          this.modalShowing = false;
          this.confirming = false;
          this.$swal({
            title: this.deletingBinLocation.name + " successfully deleted!",
            type: "success",
            confirmButtonText: "Okay"
          });
          this.deletingBinLocation = null;
          this.isDeletingBinLocationActive = false;
          this.loadBinLocations();
        });
    },
    deleteCancelled() {
      this.modalShowing = false;
      this.deletingBinLocation = null;
      this.isDeletingBinLocationActive = false;
    },

    showDepotBinLocationForm() {
      this.depotBinLocationFormShowing = true;
      this.updatingDepotBinLocation = null;
    },
    depotBinLocationFormCancelled() {
      this.depotBinLocationFormShowing = false;
      this.updatingDepotBinLocation = null;
    },
    depotBinLocationFormSubmitted() {
      this.depotBinLocationFormShowing = false;
      this.loadBinLocations();
    },
    updateDepotBinLocation(depotBinLocation) {
      this.updatingDepotBinLocation = depotBinLocation;
      this.depotBinLocationFormShowing = true;
    },
    deleteDepotBinLocation(depotBinLocation) {
      this.modalShowing = true;
      this.deletingDepotBinLocation = depotBinLocation;
    },
    deleteDepotBinLocationConfirmed() {
      this.confirming = true;
      axios
        .delete(
          "/depot/booking-binlocation/" + this.deletingDepotBinLocation.id
        )
        .then(response => {
          this.modalShowing = false;
          this.confirming = false;
          this.$swal({
            title: "Depot bin location successfully deleted!",
            type: "success",
            confirmButtonText: "Okay"
          });
          this.deletingDepotBinLocation = null;
          this.loadBinLocations();
        });
    },
    deleteDepotBinLocationCancelled() {
      this.modalShowing = false;
      this.deletingDepotBinLocation = null;
    }
  },
  watch: {
    filteredDepot() {
      this.loadBinLocations();
    }
  }
};
</script>

<style scoped>
.page-heading__header {
  font-size: 2rem;
}
</style>
